import { Button, Typography } from "antd";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCheckCircle, AiOutlineMail } from "react-icons/ai";
import { useNavigate } from "react-router";

import { Loading } from "design-system/components/Loading";
import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";
import { trackAmplitudeEvent } from "sandbox/analytics/amplitude";
import { useVerifyEmailQuery } from "sandbox/api/hooks/useSandboxQueries";
import { sandboxPaths } from "sandbox/paths";
import { getErrorStatus } from "shell/api/amplitudeCore";
import { useQueryParams } from "shell/api/hooks";
import { extractRestErrorMessage } from "shell/api/utils";

import s from "./VerifyEmailConfirmScreen.module.css";

const CONTACT_URL = "https://wwww.thredd.ai/get-in-touch";
const REDIRECT_DELAY_MS = 2000;

export const VerifyEmailConfirmScreen = () => {
  const { t } = useTranslation("sandbox");
  const navigate = useNavigate();
  const { getQueryParam } = useQueryParams();
  const token = getQueryParam("token");
  const hasRedirected = useRef(false);

  const {
    data,
    dataUpdatedAt,
    isError,
    isFetched,
    isFetching,
    error,
    errorUpdatedAt,
    refetch,
  } = useVerifyEmailQuery(token ?? "");

  const isSuccess = isFetched && !isError && data;
  const errorMessage =
    extractRestErrorMessage((error as { body?: unknown })?.body) ??
    (error as Error | undefined)?.message ??
    t("components.verifyEmailConfirmScreen.errorDescription");

  useEffect(() => {
    if (token && data && dataUpdatedAt) {
      trackAmplitudeEvent("Email Verified", { outcome: "success" });
    }
  }, [data, dataUpdatedAt, token]);

  useEffect(() => {
    if (token && errorUpdatedAt) {
      trackAmplitudeEvent("Email Verified", {
        outcome: "failure",
        error_status: getErrorStatus(error),
      });
    }
  }, [error, errorUpdatedAt, token]);

  useEffect(() => {
    if (isSuccess && !hasRedirected.current) {
      hasRedirected.current = true;
      const timeout = setTimeout(() => {
        navigate(sandboxPaths.login, { replace: true });
      }, REDIRECT_DELAY_MS);
      return () => clearTimeout(timeout);
    }
  }, [isSuccess, navigate]);

  if (!token) {
    return (
      <>
        <div className={s.iconBadgeError} aria-hidden>
          <AiOutlineMail />
        </div>
        <Typography.Title level={2} className={s.heading}>
          {t("components.verifyEmailConfirmScreen.missingTokenTitle")}
        </Typography.Title>
        <Typography.Paragraph className={s.subtitle}>
          {t("components.verifyEmailConfirmScreen.missingTokenDescription")}
        </Typography.Paragraph>
        <Button
          type="primary"
          size="large"
          block
          className={s.contactButton}
          href={CONTACT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("components.verifyEmailConfirmScreen.contactUs")}
        </Button>
      </>
    );
  }

  if (isFetching) {
    return (
      <Loading
        isContained
        size="large"
        tip={t("components.verifyEmailConfirmScreen.verifying")}
      />
    );
  }

  if (isSuccess) {
    const isAlreadyVerified = data?.message === "Email already verified";
    return (
      <>
        <div className={s.iconBadgeSuccess} aria-hidden>
          <AiOutlineCheckCircle />
        </div>
        <Typography.Title level={2} className={s.heading}>
          {isAlreadyVerified
            ? t("components.verifyEmailConfirmScreen.alreadyVerifiedTitle")
            : t("components.verifyEmailConfirmScreen.successTitle")}
        </Typography.Title>
        <Typography.Paragraph className={s.subtitle}>
          {isAlreadyVerified
            ? t(
                "components.verifyEmailConfirmScreen.alreadyVerifiedDescription",
              )
            : t("components.verifyEmailConfirmScreen.successDescription")}
        </Typography.Paragraph>
      </>
    );
  }

  return (
    <>
      <section className={s.errorAlert}>
        <QueryErrorAlert
          title={t("components.verifyEmailConfirmScreen.errorTitle")}
          description={errorMessage}
          onRetry={() => refetch()}
          retryLoading={isFetching}
        />
      </section>
      <Button
        type="default"
        size="large"
        block
        className={s.contactButton}
        href={CONTACT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("components.verifyEmailConfirmScreen.contactUs")}
      </Button>
    </>
  );
};
